/** @jsx jsx */

import {
  Fragment as _,
  useContext,
  useState,
  cloneElement,
  useRef,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext as InteractionContext } from './interactionContext';
import { useDispatch } from 'react-redux';
import { submitToCollect } from 'services/submitToCollect';
import { unlockInteraction } from 'services/unlockInteraction';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import { Report } from 'store/store';
import pubSub from 'services/pubSub';
import { TextEntry } from 'components/UI/TextEntry';

import { HostingContext } from 'hosting/context';
import { textEntryResponseEvent } from 'hosting/events';

type Result = 'correct' | 'incorrect' | 'attempt' | undefined;
enum SubmitState {
  Initial = 0,
  Resolved = 2,
}

const TextEntryQuiz = props => {
  const interactionContext = useContext(InteractionContext);
  const hostingCtx = useContext(HostingContext);

  console.log(
    'IS THIS THE TEXT ENTRY ??? OR IS ALWAYS QUIZ !?!?',
    interactionContext.props,
    props
  );

  const {
    messageTextColor = '#000',
    messageBorderColor = '#d2dff9',
    fontFamily = 'inherit',
    messageFontSize = 16,
    score,
    correctAnswer = '',
    useMediaInColumn = true,
    lockDown = 0,
    numberOfAttempts = 1,
    showAttemptNumber = true,
    useMediaInFeedback = true,
    showCorrectAnswer = true,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
    includeFeedback = true,
    includeQuestionTextPlusImage,
    correctAnswerRequirement = 'any',
  } = interactionContext.props;

  // console.log(interactionContext.props);

  const { children } = props;

  let [txt, submitBtn, feedback] = children;

  const [textAreaStyle, setTextAreaStyle] = useState<{
    height?: number;
    overflow?: string;
  }>({});

  const [attempt, setAttempt] = useState<number>(0);
  const [result, setResult] = useState<Result>(undefined);
  const [resolved, setResolved] = useState<SubmitState>(0);
  const [isLive, setIsLive] = useState<boolean | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);

  const [hasFocus, setHasFocus] = useState<boolean>(false);

  const scoringQuizzes = useScoringQuizzes();
  const dispatch = useDispatch();

  const message = useRef<string>('');
  const [_message_, setMessage] = useState('');
  message.current = _message_;

  submitBtn = cloneElement(submitBtn, {
    onClick() {
      const { id } = interactionContext;

      const questionTextElement = document.querySelector(
        `#text-entry-quiz-question-${id}`
      ) as HTMLDivElement;
      const chameleonTextElement = questionTextElement?.querySelector(
        `.chameleon-text p`
      ) as HTMLElement;

      // question might be an image ? get its alt ?
      let question = '';
      if (chameleonTextElement) {
        question = chameleonTextElement.innerText;
      }

      let correctAnswerStringToArray;
      let isCorrect;

      // if a correctAnswer has been provided. and not undefined (or empty string)
      if (!!correctAnswer) {
        correctAnswerStringToArray = correctAnswer
          .replace(/ /g, '')
          .split(/,/g)
          .map(entry => {
            return entry.toLowerCase();
            // .trim(); < no need to trim if we're removing all spaces
          });

        isCorrect = correctAnswerStringToArray[
          correctAnswerRequirement === 'any' ? 'some' : 'every'
        ](entry => {
          const regEx = new RegExp(entry, 'g');
          return regEx.test(message.current.replace(/ /g, '').toLowerCase());
        });
      } else {
        isCorrect = true;
      }

      let result: Result = isCorrect ? 'correct' : 'incorrect';

      const action = {
        id,
        type: 'fill-in',
        question,
        reportable: 'fillin',
        response: message?.current,
        correct: `${correctAnswerRequirement} of: ${correctAnswer}`,
        result: isCorrect ? 1 : 0,
      } as Report;

      hostingCtx?.data(
        textEntryResponseEvent(id, {
          value: message?.current,
          attemptNumber: attempt + 1,
          success: isCorrect ? true : false,
        })
      );

      // dispatch if scoring and there's no scoringQuizzes etc

      let shouldDispatchScore = Boolean(score);

      //
      if (scoringQuizzes.length) {
        shouldDispatchScore = false;
      }

      if (shouldDispatchScore) {
        dispatch(updateScoreAction(id, action.result));
      }

      dispatch(addScormResponseAction(action));

      const unlock = result === 'correct' || attempt === numberOfAttempts - 1;

      if (unlock) {
        unlockInteraction(id);
        setIsLive(false);

        setResolved(2);
      }

      if (result !== 'correct') {
        if (attempt < numberOfAttempts - 1) {
          setAttempt(attempt => {
            return (attempt += 1);
          });
          result = 'attempt';
        } else {
          result = 'incorrect';
        }
      }

      setResult(result);
      setShowFeedback(true);
    },
  });

  useEffect(() => {
    if (isSubmitted) {
      setShowFeedback(true);
    }
  }, [isSubmitted]);

  useEffect(() => {
    const pS = pubSub.subscribe(
      `TEXT_ENTRY_QUIZ_${interactionContext.id}`,
      ({ showFeedback }) => {
        setResult(showFeedback);
        setShowFeedback(true);
      }
    );

    return pS;
  }, []);

  const sharedStyles = {
    width: '100%',
    height: '100%',
    maxHeight: 240,
    padding: '5px 5px 5px 20px',
    fontFamily,
    fontSize: messageFontSize,
    color: messageTextColor,
  };

  return (
    <div
      style={{
        width: '100%',
        ...(resolved && {
          pointerEvents: 'none',
        }),
      }}
    >
      <div
        id={`text-entry-quiz-question-${interactionContext.id}`}
        style={{ margin: '0 0 20px 0' }}
      >
        {txt}
      </div>
      <div
        style={{
          position: 'relative',
          maxHeight: sharedStyles.maxHeight,
        }}
      >
        <TextEntry
          message={message.current}
          onUpdate={value => {
            setMessage(value);
            setIsLive(!!value);
          }}
          messageContainer={null}
          messageUnderline={messageBorderColor}
          messageTextColor={messageTextColor}
          // messageTextBackgroundColor={
          //   `rgba(${r}, ${g}, ${b}, ${a})` || messageTextBackgroundColor
          // }
          messageTextBackgroundColor={'transparent'}
          messageBorderRadius={0}
          messageBorderColor={messageBorderColor}
          messageFontFamily={fontFamily}
          messageFontSize={messageFontSize}
          textAlign={'left'}
          maxLinesBeforeScrolling={4}
          // maxHeight={115}
          placeholderText="Type your answer here..."
        />
      </div>

      <div
        style={{
          width: '100%',
          margin: '20px 0 0 0',
        }}
      >
        <div
          style={{
            ...(!window.dev() && {
              opacity: isLive ? 1 : 0.25,
              pointerEvents: isLive ? 'auto' : 'none',
              transition: 'opacity 0.5s',
            }),
          }}
        >
          {submitBtn}
        </div>
      </div>

      {includeFeedback &&
        showFeedback &&
        cloneElement(feedback, {
          result,
          showAttemptNumber,
          attempt,
          numberOfAttempts,
          navigationBehaviour,
          onClose() {
            setShowFeedback(false);
          },
          useMediaInFeedback,
        })}
    </div>
  );
};

export default TextEntryQuiz;
